import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import type {
  GetSessionsForViewerPayload,
  GetSessionsForViewerResponse,
} from '@usacsmideo/types';
import { functions } from '../constants/firebaseModular';
import { useAuth } from '../contexts/AuthContext';

/**
 * ECN Sessions Surface (CU-86aj1c9ev) — the read path for the `/sessions` page.
 *
 * `emergencyAccessSessions` is Admin-SDK-only (no client read rules — PHI stays
 * server-side), so we cannot listen to it from the client. This hook wraps the
 * `getSessionsForViewer` callable, which returns the caller's own sessions
 * ("My Scans") plus the sessions of patients they're an accepted agent of
 * ("My Family's Scans"), already scoped + flattened to epoch-ms server-side.
 *
 * The payload is empty by design — scope is derived from the auth token, never
 * from client args. The recency filter (24h/week/month/all) is applied
 * client-side over the all-time result this returns.
 */
export function useSessionsForViewer({ enabled = true }: { enabled?: boolean } = {}) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  return useQuery<GetSessionsForViewerResponse>({
    queryKey: ['sessionsForViewer', uid],
    enabled: enabled && !!uid,
    queryFn: async () => {
      const call = httpsCallable<
        GetSessionsForViewerPayload,
        GetSessionsForViewerResponse
      >(functions, 'getSessionsForViewer');
      const { data } = await call({});
      return data;
    },
  });
}

export default useSessionsForViewer;
